import { Flex } from '@repo/ui/Flex';
import { Button } from '@repo/ui/Button';
import { ItemCard } from '../../ItemCard/ItemCard';
import { Text, rawString } from '../../Language/Language';
import type { LanguageCore } from '../../Language/Language';
import type { ShipmentScopeItemGroupProps } from './ShipmentScopeItemGroup.types';
import s from './ShipmentScopeItemGroup.module.css';

const DetailItem = ({
  title,
  subtitle,
}: {
  title: LanguageCore;
  subtitle: LanguageCore;
}) => (
  <Flex direction="row" gap="g-4" className={s.detailItem}>
    <strong className={s.title}>
      <Text {...title} />
      {':'}
    </strong>
    <Text {...subtitle} />
  </Flex>
);

export const ShipmentScopeItemGroup = ({
  primaryItem,
  otherItems,
  reason,
  resolution,
  onEdit,
  formData,
}: ShipmentScopeItemGroupProps) => {
  const allItems = [primaryItem, ...otherItems];

  return (
    <Flex direction="column" gap="g-16" data-testid="shipmentGroup">
      {/* Render each item as its own card */}
      {allItems.map((item) => {
        // For shipment scope items, show the selected quantity from form data
        // Virtual items should now have the correct shipment quantity in their itemSelection
        const itemData = formData?.items?.[item.id];
        const quantity =
          itemData?.itemSelection?.quantity ?? item?.quantity?.remaining ?? 1;
        const otherShipmentItems = allItems.filter(
          (otherItem) => otherItem.id !== item.id,
        );

        return (
          <ItemCard
            key={item.id}
            item={item}
            additionalDetails={[
              {
                name:
                  rawString({ langKey: 'Global.item.attribute.quantity' }) ??
                  '',
                value: quantity.toString(),
              },
            ]}
            collapsible
            defaultCollapsed={true}
            imageSize="sm"
          >
            <Flex className={s.details} direction="column">
              {reason && (
                <DetailItem
                  title={{
                    langKey: 'Global.reasons.shipmentPreview.reason',
                  }}
                  subtitle={{
                    langKey: [
                      `Global.reason.${reason.code}.title`,
                      `Global.reason.${reason.title}.title`,
                    ],
                    fallback: reason.title,
                  }}
                />
              )}
              {resolution && (
                <DetailItem
                  title={{
                    langKey: 'Global.reasons.shipmentPreview.outcome',
                  }}
                  subtitle={{
                    langKey: `Global.reviewItemSummary.resolution.${resolution}`,
                  }}
                />
              )}

              {/* Other items in this package */}
              {otherShipmentItems.length > 0 && (
                <div className={s.otherItemsSection}>
                  <h3 className={s.otherItemsHeader}>
                    <Text
                      langKey="Global.reasons.shipmentPreview.otherItemsInPackage"
                      replacementTokens={{
                        itemCount: otherShipmentItems.length.toString(),
                        plural: otherShipmentItems.length === 1 ? '' : 's',
                      }}
                    />
                  </h3>
                  <Flex direction="column" gap="g-8">
                    {otherShipmentItems.map((otherItem) => {
                      // Get the actual quantity from form data, fallback to item remaining quantity
                      const otherItemData = formData?.items?.[otherItem.id];
                      const otherQuantity =
                        otherItemData?.itemSelection?.quantity ??
                        otherItem?.quantity?.remaining ??
                        1;

                      return (
                        <ItemCard
                          key={otherItem.id}
                          item={otherItem}
                          additionalDetails={[
                            {
                              name:
                                rawString({
                                  langKey: 'Global.item.attribute.quantity',
                                }) ?? '',
                              value: otherQuantity.toString(),
                            },
                          ]}
                          imageSize="sm"
                          className={s.otherItemCard}
                          data-testid="shipmentOtherItem"
                        />
                      );
                    })}
                  </Flex>
                </div>
              )}

              <Button
                variant="secondary"
                size="lg"
                onClick={onEdit}
                className={s.edit}
                isFullWidth
              >
                <Text langKey={['Global.review.items.edit', 'Global.edit']} />
              </Button>
            </Flex>
          </ItemCard>
        );
      })}
    </Flex>
  );
};
